import { Trans, useTranslation } from 'react-i18next';
import type { PlayerListVisibility } from '@swiss-magic/shared-types';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TournamentFormController } from './useTournamentFormController';

// 076 follow-up — what used to be a single section is now three
// independent components so TournamentFormPage can place each in
// the logical group it belongs to:
//   - RegistrationToggles: public + online registration checkboxes
//   - ResultConfirmationSelect: who finalises a player-reported result
//   - PlayerListVisibilitySection: list / count / hidden
// Same controls, same controller, just regrouped on the page.

interface Props {
  form: TournamentFormController;
}

export function RegistrationToggles({ form }: Props) {
  const { t } = useTranslation('director');

  return (
    <div className="space-y-2">
      <Label>{t('tournament_form.visibility_section_label')}</Label>
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="public-tournament-checkbox"
            checked={!form.isPrivate}
            onChange={(e) => form.setIsPrivate(!e.target.checked)}
          />
          {t('tournament_form.public_tournament')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="online-registration-checkbox"
            checked={form.onlineRegistration}
            onChange={(e) => form.setOnlineRegistration(e.target.checked)}
          />
          {t('tournament_form.online_registration')}
        </label>
      </div>
    </div>
  );
}

export function ResultConfirmationSelect({ form }: Props) {
  const { t } = useTranslation('director');

  // Story 010b — locked once the tournament is in_progress
  // (round 1 has been generated).
  return (
    <div className="space-y-2">
      <Label htmlFor="result-confirmation-mode">
        {t('tournament_form.result_confirmation.label')}
      </Label>
      <Select
        value={form.resultConfirmationMode}
        disabled={
          form.tournamentStatus === 'in_progress' ||
          form.tournamentStatus === 'completed'
        }
        onValueChange={(v) =>
          form.setResultConfirmationMode(
            v as 'organizer_confirmation' | 'player_confirmation',
          )
        }
      >
        <SelectTrigger
          id="result-confirmation-mode"
          data-testid="result-confirmation-mode-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="organizer_confirmation">
            {t('tournament_form.result_confirmation.organizer_option')}
          </SelectItem>
          <SelectItem value="player_confirmation">
            {t('tournament_form.result_confirmation.player_option')}
          </SelectItem>
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        <Trans
          i18nKey="tournament_form.result_confirmation.description"
          t={t}
          components={{ strong: <span className="font-medium" /> }}
        />
      </p>
    </div>
  );
}

interface PlayerListVisibilitySectionProps {
  form: TournamentFormController;
}

export function PlayerListVisibilitySection({
  form,
}: PlayerListVisibilitySectionProps) {
  const { t } = useTranslation('director');

  return (
    <div className="space-y-2">
      <Label>{t('tournament_form.player_list_visibility_label')}</Label>
      <Select
        value={form.playerListVisibility}
        onValueChange={(v) =>
          form.setPlayerListVisibility(v as PlayerListVisibility)
        }
      >
        <SelectTrigger data-testid="player-list-visibility-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="list">{t('tournament_form.player_list_visibility.list')}</SelectItem>
          <SelectItem value="count">{t('tournament_form.player_list_visibility.count')}</SelectItem>
          <SelectItem value="hidden">{t('tournament_form.player_list_visibility.hidden')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
